import React, { ReactNode } from 'react';
import { IonModal, IonText } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { RevenueCatModel } from './revenue-cat/RevenueCatModel';
import { RevenueCatSDKPurchaseError } from './revenue-cat/PurchasesErrorAdapter';
import { AlertPresenter } from '../app/AlertBinder';
import { PaywallDialogViewModel } from './PaywallDialogViewModel';
import { OperatorState } from '../profile/OperatorState';
import { Spinner } from '../components/spinner/Spinner';
import { StandardDialogContent } from './StandardDialogContent';
import { PromotionDialogContent } from './PromotionDialogContent';
import { NewUserDialogContent } from './NewUserDialogContent';
import { FloatingCloseButton } from '../components/button/FloatingCloseButton';

export const PaywallDialog: React.FC = () => {
    const alert = useInstance(AlertPresenter);

    const revenueCat = useInstance(RevenueCatModel);
    useSubscription(() => revenueCat);

    const operator = useInstance(OperatorState);
    useSubscription(() => operator);

    const dialogVM = useInstance(PaywallDialogViewModel);
    useSubscription(() => dialogVM);

    const trialDays = 7;
    const term = dialogVM.state.paymentTerm;

    const hasUpgraded = revenueCat.hasUpgraded();
    const isEligible = revenueCat.isEligibleForTrial();

    const showNewUserContent = !hasUpgraded && isEligible && dialogVM.isNewUser();
    const showPromotion = !hasUpgraded && isEligible && dialogVM.isPromotionActive();

    const upgrade = async () => {
        try {
            dialogVM.setState({ isWorking: true });

            // guest must sign up before continuing
            let message: ReactNode;
            if (!isEligible || (showPromotion && term === 'annual')) {
                // annual promotion doesn't have free trial
                message = <>Sign up now to get all the benefits of <IonText color="primary">AutoPylot+</IonText></>;
            } else {
                message = <>Sign up now and redeem your<br/>free {trialDays}-day trial of <IonText color="primary">AutoPylot+</IonText></>;
            }
            const imageUrl = '/assets/images/auth/paywall-auth-incentive-v1.png';
            if (!operator.ensureLoggedIn({ message, imageUrl, from: 'Paywall', returnTo: dialogVM.getReturnToUrl({ term }) })) {
                return;
            }

            await revenueCat.upgrade(term);
            dialogVM.close();
        } catch (e: any) {
            if (e && e.errorType === 'RevenueCatSDKPurchaseError' && (e as RevenueCatSDKPurchaseError).cancelled){
                // then do nothing
            } else {
                alert.showAndLogError(e);
            }
        } finally {
            dialogVM.setState({ isWorking: false });
        }
    };

    const restore = async () => {
        try {
            dialogVM.setState({ isWorking: true });
            await revenueCat.restore();
            dialogVM.close();
        } catch (e: any) {
            if (e && e.errorType === 'RevenueCatSDKPurchaseError' && (e as RevenueCatSDKPurchaseError).cancelled){
                // then do nothing
            } else {
                alert.showAndLogError(e);
            }
        } finally {
            dialogVM.setState({ isWorking: false });
        }
    };

    return (
        <IonModal
            data-id="autopylot-plus"
            isOpen={dialogVM.state.isOpen}
            onWillDismiss={dialogVM.close}
            keepContentsMounted={true}
        >
            <FloatingCloseButton onClick={dialogVM.close} />

            {/* check isOpen so these remount each time and fire their PaywallOpened events */}
            {dialogVM.state.isOpen &&
                <>
                    {showPromotion ? (
                        <PromotionDialogContent onUpgrade={upgrade} onRestore={restore} />
                    ) : showNewUserContent ? (
                        <NewUserDialogContent onUpgrade={upgrade} onRestore={restore} />
                    ) : (
                        <StandardDialogContent onUpgrade={upgrade} onRestore={restore} />
                    )}

                    {dialogVM.state.isWorking &&
                        <Spinner centered />
                    }
                </>
            }
        </IonModal>
    );
};
